import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useLiff } from '@/hooks/useLiff'
import { apiClient, type Attendance } from '@/lib/api'

interface TodayData {
  attendance: Attendance | null
  ongoingBreak: any | null
  currentTime: string
}

export default function AttendancePage() {
  const { accessToken, isReady } = useLiff()
  const [todayData, setTodayData] = useState<TodayData | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [currentTime, setCurrentTime] = useState(new Date())

  // 現在時刻の更新
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  const loadTodayData = useCallback(async () => {
    try {
      const response = await apiClient.getTodayAttendance()
      // API response has the data nested, so extract it
      const data: TodayData = {
        attendance: response.attendance,
        ongoingBreak: response.ongoingBreak,
        currentTime: response.currentTime
      }
      setTodayData(data)
    } catch (error) {
      console.error('Failed to load today data:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isReady && accessToken) {
      apiClient.setAccessToken(accessToken)
      loadTodayData()
    }
  }, [isReady, accessToken, loadTodayData])

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('ja-JP', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short'
    })
  }

  const formatMinutesToHours = (minutes: number): string => {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return `${hours}時間${mins.toString().padStart(2, '0')}分`
  }

  const handleAction = async (action: 'clock-in' | 'clock-out' | 'break-start' | 'break-end') => {
    setActionLoading(action)
    try {
      let response
      switch (action) {
        case 'clock-in':
          response = await apiClient.clockIn()
          break
        case 'clock-out':
          response = await apiClient.clockOut()
          break
        case 'break-start':
          response = await apiClient.breakStart()
          break
        case 'break-end':
          response = await apiClient.breakEnd()
          break
      }
      
      // 成功メッセージを表示
      if (response.message) {
        alert(response.message)
      }
      
      // データを再読み込み
      await loadTodayData()
    } catch (error) {
      console.error(`Action ${action} failed:`, error)
      alert(error instanceof Error ? error.message : 'エラーが発生しました')
    } finally {
      setActionLoading(null)
    }
  }

  if (!isReady || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    )
  }

  const isWorking = todayData?.attendance?.clock_in && !todayData?.attendance?.clock_out
  const isOnBreak = todayData?.ongoingBreak !== null

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* ヘッダー */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center space-x-3">
            <Link to="/" className="text-blue-500 text-xl">←</Link>
            <div>
              <h1 className="text-lg font-bold text-gray-900">出退勤</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="p-4 pb-20">
        {/* 現在日時表示 */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-6 text-center">
          <div className="text-2xl font-bold text-gray-900 mb-2">
            {formatDate(currentTime)}
          </div>
          <div className="text-4xl font-mono font-bold text-blue-600">
            {formatTime(currentTime)}
          </div>
        </div>

        {/* メイン打刻ボタン */}
        <div className="mb-6">
          {!isWorking ? (
            // 出勤ボタン
            <button
              onClick={() => handleAction('clock-in')}
              disabled={actionLoading === 'clock-in'}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl shadow-lg p-8 font-bold text-2xl active:scale-95 transition-transform disabled:opacity-50"
            >
              {actionLoading === 'clock-in' ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mr-3"></div>
                  処理中...
                </div>
              ) : (
                <>
                  <div className="text-4xl mb-2">🟢</div>
                  出勤する
                </>
              )}
            </button>
          ) : (
            // 退勤ボタン
            <button
              onClick={() => handleAction('clock-out')}
              disabled={actionLoading === 'clock-out'}
              className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl shadow-lg p-8 font-bold text-2xl active:scale-95 transition-transform disabled:opacity-50"
            >
              {actionLoading === 'clock-out' ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mr-3"></div>
                  処理中...
                </div>
              ) : (
                <>
                  <div className="text-4xl mb-2">🔴</div>
                  退勤する
                </>
              )}
            </button>
          )}
        </div>

        {/* 休憩ボタン（勤務中のみ表示） */}
        {isWorking && (
          <div className="grid grid-cols-2 gap-4 mb-6">
            {!isOnBreak ? (
              <button
                onClick={() => handleAction('break-start')}
                disabled={actionLoading === 'break-start'}
                className="bg-yellow-500 text-white rounded-xl shadow-lg p-4 font-bold active:scale-95 transition-transform disabled:opacity-50"
              >
                {actionLoading === 'break-start' ? '処理中...' : '🟡 休憩開始'}
              </button>
            ) : (
              <button
                onClick={() => handleAction('break-end')}
                disabled={actionLoading === 'break-end'}
                className="bg-orange-500 text-white rounded-xl shadow-lg p-4 font-bold active:scale-95 transition-transform disabled:opacity-50"
              >
                {actionLoading === 'break-end' ? '処理中...' : '🟠 休憩終了'}
              </button>
            )}
          </div>
        )}

        {/* 今日の記録 */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center">
            <span className="text-xl mr-2">📝</span>
            今日の記録
          </h3>
          
          {todayData?.attendance ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">出勤時刻</span>
                <span className="font-mono font-bold">
                  {todayData.attendance.clock_in || '未打刻'}
                </span>
              </div>
              
              {todayData.attendance.clock_out && (
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">退勤時刻</span>
                  <span className="font-mono font-bold">
                    {todayData.attendance.clock_out}
                  </span>
                </div>
              )}
              
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">休憩時間</span>
                <span className="font-mono font-bold">
                  {formatMinutesToHours(todayData.attendance.break_min)}
                </span>
              </div>
              
              {todayData.attendance.work_min !== null && todayData.attendance.work_min !== undefined && (
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 font-bold">実働時間</span>
                  <span className="font-mono font-bold text-blue-600">
                    {formatMinutesToHours(todayData.attendance.work_min)}
                  </span>
                </div>
              )}

              {isOnBreak && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
                  <div className="flex items-center text-yellow-800">
                    <span className="text-lg mr-2">⏸️</span>
                    <span className="font-bold">休憩中</span>
                  </div>
                  <div className="text-sm text-yellow-700 mt-1">
                    開始時刻: {todayData.ongoingBreak.break_start}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <div className="text-4xl mb-3">📅</div>
              <p>今日はまだ打刻していません</p>
            </div>
          )}
        </div>

        {/* 履歴リンク */}
        <div className="mt-6">
          <Link 
            to="/attendance/history"
            className="block w-full bg-gray-100 text-gray-700 rounded-xl p-4 text-center font-medium active:scale-95 transition-transform"
          >
            📊 勤怠履歴を見る
          </Link>
        </div>
      </main>
    </div>
  )
}